// The event lane: high-impact releases as small flags in ONE strip at the
// bottom edge of the pane -- never as text floating over candles. The
// research was blunt: mid-chart event banners are the single most-complained
// clutter in charting, and our own screens proved it (a clipped yellow smear
// over the top edge of the pane, on the owner's phone).
//
// The lane says WHERE. The readout's hover chip says WHAT. The calendar page
// says everything. Text never touches the candles.

const LANE_H = 16;          // css px, at the pane's bottom edge
const CLUSTER_PX = 14;      // flags closer than this collapse into a badge

class EventLaneRenderer {
  constructor(source) {
    this._source = source;
  }

  draw(target) {
    const source = this._source;
    const events = source._events;
    if (!source._enabled || !source._chart || !events.length) return;
    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hRatio = scope.horizontalPixelRatio;
      const vRatio = scope.verticalPixelRatio;
      const height = scope.bitmapSize.height;
      const timeScale = source._chart.timeScale();

      // Project every event onto the pane; keep the visible ones.
      const xs = [];
      for (const event of events) {
        let x = null;
        try { x = timeScale.timeToCoordinate(event.time); } catch (e) { continue; }
        if (x === null || x < 0) continue;
        xs.push({ x, count: 1 });
      }
      if (!xs.length) return;
      xs.sort((a, b) => a.x - b.x);

      // Cluster: a data-heavy week must not become a picket fence.
      const clusters = [xs[0]];
      for (let i = 1; i < xs.length; i += 1) {
        const last = clusters[clusters.length - 1];
        if (xs[i].x - last.x <= CLUSTER_PX) last.count += 1;
        else clusters.push(xs[i]);
      }

      const laneTop = height - LANE_H * vRatio;
      ctx.save();
      for (const cluster of clusters) {
        const x = cluster.x * hRatio;
        // The flag: a stem and a small pennant, gold like the calendar.
        ctx.strokeStyle = "rgba(201, 154, 58, 0.9)";
        ctx.lineWidth = Math.max(1, 1 * hRatio);
        ctx.beginPath();
        ctx.moveTo(x, laneTop + 3 * vRatio);
        ctx.lineTo(x, height - 2 * vRatio);
        ctx.stroke();
        ctx.fillStyle = "rgba(201, 154, 58, 0.9)";
        ctx.beginPath();
        ctx.moveTo(x, laneTop + 3 * vRatio);
        ctx.lineTo(x + 6 * hRatio, laneTop + 5.5 * vRatio);
        ctx.lineTo(x, laneTop + 8 * vRatio);
        ctx.closePath();
        ctx.fill();
        if (cluster.count > 1) {
          ctx.font = `${Math.round(8 * vRatio)}px "Inter Tight", system-ui, sans-serif`;
          ctx.textBaseline = "bottom";
          ctx.fillText(String(cluster.count), x + 2 * hRatio, laneTop + 2 * vRatio);
        }
      }
      ctx.restore();
    });
  }
}

class EventLanePaneView {
  constructor(source) {
    this._source = source;
    this._renderer = new EventLaneRenderer(source);
  }
  zOrder() { return "top"; }
  update() {}
  renderer() { return this._renderer; }
}

export class EventLane {
  constructor() {
    this._enabled = true;
    this._chart = null;
    this._requestUpdate = null;
    this._events = [];
    this._paneViews = [new EventLanePaneView(this)];
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
  }

  paneViews() { return this._paneViews; }
  updateAllViews() { this._paneViews.forEach((v) => v.update()); }
  autoscaleInfo() { return null; }

  // events: [{ time: <actual bar time, seconds> }]
  setEvents(events) {
    this._events = Array.isArray(events) ? events : [];
    if (this._requestUpdate) this._requestUpdate();
  }

  setEnabled(on) {
    this._enabled = !!on;
    if (this._requestUpdate) this._requestUpdate();
  }
}
